import React, { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  FileText,
  User,
  GraduationCap,
  Briefcase,
  Award,
  ShieldCheck,
  Clock,
  ChevronLeft,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  XCircle,
  AlertCircle,
  MapPin,
  Mail,
  Phone,
} from "lucide-react";
import {
  useGetProbationerApplicationDetailsQuery,
  useReturnProbationerApplicationMutation,
  useRefereeApproveProbationerApplicationMutation,
  useRefereeRejectProbationerApplicationMutation,
} from "../api/probationerApplicationApi";
import { IN_REVIEW, statusLabel } from "../utils/applicationStatus";
import { fileUrl } from "../utils/files";
import { useAccess } from "../../../utils/useAccess";
import { PERMS } from "../../../utils/perms";

/**
 * =========================
 * AUTH & DATA HELPERS
 * =========================
 */

function getAuthRole(): "admin" | "member" {
  try {
    const raw = localStorage.getItem("logbook_auth_user_v2");
    const u = raw ? JSON.parse(raw) : null;
    const rolesRaw =
      u?.roles || u?.user?.roles || u?.data?.roles || u?.user?.role;
    let roles: string[] = [];
    if (Array.isArray(rolesRaw))
      roles = rolesRaw.map((x) => String((x as any)?.name ?? x).toLowerCase());
    else if (typeof rolesRaw === "string") roles = [rolesRaw.toLowerCase()];
    if (roles.includes("admin") || roles.includes("super_admin"))
      return "admin";
    return "member";
  } catch {
    return "member";
  }
}

function getAuthMemberId(): string | null {
  try {
    const raw = localStorage.getItem("logbook_auth_user_v2");
    const u = raw ? JSON.parse(raw) : null;
    const mId =
      u?.member_id || u?.member?.id || u?.user?.member_id || u?.data?.member_id;
    return mId ? String(mId) : null;
  } catch {
    return null;
  }
}

function safeMsg(x: any) {
  return String(
    x?.data?.message || x?.message || x?.error || "Something went wrong",
  );
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

function normalizeDecision(decision?: string) {
  const d = String(decision || "pending")
    .toLowerCase()
    .trim();
  return d === "accepted" || d === "approved"
    ? "approved"
    : d === "rejected"
    ? "rejected"
    : "pending";
}

/**
 * =========================
 * UI ATOMS
 * =========================
 */

function Row({ k, v }: { k: string; v: any }) {
  const val =
    v === null || v === undefined || v === ""
      ? "—"
      : typeof v === "boolean"
      ? v
        ? "Yes"
        : "No"
      : String(v);
  return (
    <div className="flex justify-between items-center gap-4 py-3 border-b border-gray-50 last:border-0">
      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
        {k}
      </div>
      <div className="text-sm font-bold text-gray-900 text-right">{val}</div>
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
  badge,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
  badge?: any;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-[32px] overflow-hidden shadow-sm">
      <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-400" />
          <h3 className="text-xs font-black uppercase tracking-widest text-gray-900">
            {title}
          </h3>
        </div>
        {badge}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function SubjectBadges({ subjects }: { subjects: any }) {
  if (!subjects) return <span className="text-gray-400">—</span>;
  const list = Array.isArray(subjects)
    ? subjects
    : Object.entries(subjects).map(([k, v]) => `${k}: ${v}`);
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {list.map((s, i) => (
        <span
          key={i}
          className="text-[10px] px-2 py-0.5 rounded-md bg-white border border-gray-100 font-bold text-gray-600 uppercase"
        >
          {String(s)}
        </span>
      ))}
    </div>
  );
}

function DocCard({
  label,
  url,
  required = false,
  compact = false,
}: {
  label: string;
  url?: string | null;
  required?: boolean;
  compact?: boolean;
}) {
  if (compact) {
    return url ? (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="p-3 bg-white rounded-xl border border-gray-100 shadow-sm text-blue-500 hover:scale-110 transition-transform flex-shrink-0"
        title={label}
      >
        <ExternalLink className="w-4 h-4" />
      </a>
    ) : (
      <div className="p-3 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-gray-300 flex-shrink-0" title={`No ${label}`}>
        <ExternalLink className="w-4 h-4" />
      </div>
    );
  }
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5 flex flex-col items-center justify-center group hover:bg-white hover:shadow-md transition-all">
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">
        {label}
      </p>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 text-xs font-black text-gray-900 uppercase tracking-widest"
        >
          <ExternalLink className="w-4 h-4 text-blue-500" /> Open Document
        </a>
      ) : (
        <span className={`text-xs font-bold uppercase ${required ? "text-orange-400" : "text-gray-300"}`}>
          {required ? "Not Uploaded" : "No File"}
        </span>
      )}
    </div>
  );
}

/**
 * =========================
 * MAIN PAGE
 * =========================
 */

export default function AdminOrRefereeApplicationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { user, can } = useAccess();
  // Which VIEW to render (admin vs member/referee) — from the real auth context.
  const role: "admin" | "member" = can.anyRole(["admin", "super_admin"])
    ? "admin"
    : "member";
  // The review privilege: only an admin holding the approve/reject permission
  // (super-admins pass via hasPermission) may see the Decision Control.
  const canReview = can.anyPerm([
    PERMS.logbook_diet.approve,
    PERMS.logbook_diet.reject,
  ]);
  const myMemberId =
    (user as any)?.member?.id ||
    (user as any)?.member_id ||
    getAuthMemberId();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } =
    useGetProbationerApplicationDetailsQuery(id as string, { skip: !id });
  const app = (data as any)?.data ?? data;

  const [returnForEdit, returnState] =
    useReturnProbationerApplicationMutation();
  const [refApprove, refApproveState] =
    useRefereeApproveProbationerApplicationMutation();
  const [refReject, refRejectState] =
    useRefereeRejectProbationerApplicationMutation();

  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);
  const [returnStatement, setReturnStatement] = useState("");
  const [refRemarks, setRefRemarks] = useState("");

  const busy =
    isLoading ||
    returnState.isLoading ||
    refApproveState.isLoading ||
    refRejectState.isLoading;

  const referees = useMemo(
    () => (Array.isArray(app?.referees) ? app.referees : []),
    [app],
  );
  const approvedCount = useMemo(
    () =>
      referees.filter((r: any) => normalizeDecision(r?.decision) === "approved")
        .length,
    [referees],
  );
  const rejectedCount = useMemo(
    () =>
      referees.filter((r: any) => normalizeDecision(r?.decision) === "rejected")
        .length,
    [referees],
  );
  const myRefRow = useMemo(
    () =>
      myMemberId
        ? referees.find((r: any) => String(r?.member_id) === String(myMemberId))
        : null,
    [referees, myMemberId],
  );
  const isMyReferral = !!myRefRow;
  const showRefereeForm =
    isMyReferral &&
    normalizeDecision(myRefRow?.decision) === "pending" &&
    !["approved", "rejected"].includes(String(app?.status).toLowerCase());

  const onReturn = async () => {
    if (!returnStatement.trim())
      return setLocalError("A review statement is required to return the application.");
    setLocalError(null);
    setLocalSuccess(null);
    try {
      const res: any = await returnForEdit({
        applicationId: id!,
        statement: returnStatement,
      }).unwrap();
      setLocalSuccess(res?.message || "Application returned to the applicant for editing.");
      setReturnStatement("");
      refetch();
    } catch (e) {
      setLocalError(safeMsg(e));
    }
  };

  const onRefAction = async (type: "approve" | "reject") => {
    if (type === "reject" && !refRemarks.trim())
      return setLocalError("Remarks are required for rejection.");
    setLocalError(null);
    setLocalSuccess(null);
    try {
      const action = type === "approve" ? refApprove : refReject;
      const res: any = await action({
        applicationId: id!,
        remarks: refRemarks,
      }).unwrap();
      setLocalSuccess(res?.message || `Referee decision recorded.`);
      setRefRemarks("");
      refetch();
    } catch (e) {
      setLocalError(safeMsg(e));
    }
  };

  if (isLoading)
    return (
      <div className="p-20 text-center font-black text-gray-400 animate-pulse uppercase tracking-widest">
        Loading Details
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8 pb-20">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
            <Link
              to={role === "admin" ? "/probationer-applications" : "/my-referee-request"}
              className="hover:text-gray-900 transition-colors"
            >
              {role === "admin" ? "Applications" : "Referee Requests"}
            </Link>
            <span className="opacity-20">/</span>
            <span className="text-gray-900 uppercase">Case Review</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-[20px] bg-gray-900 flex items-center justify-center shadow-xl shadow-gray-200">
              <FileText className="text-white w-7 h-7" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <h1 className="text-3xl font-black text-gray-900 tracking-tight">
                  {app?.surname} {app?.other_names}
                </h1>
                {app?.category?.name && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-200">
                    <Award className="w-3 h-3" />
                    {app.category.name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs font-mono text-gray-400 uppercase tracking-tighter">
                  REF: {id}
                </span>
                <span className="h-1 w-1 rounded-full bg-gray-300" />
                <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">
                  {role} VIEW
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="px-5 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-gray-50 transition-all shadow-sm"
          >
            <ChevronLeft className="w-4 h-4 inline mr-1" /> Back
          </button>
          <button
            onClick={() => refetch()}
            disabled={busy}
            className="p-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all shadow-sm"
          >
            <RefreshCw
              className={`w-5 h-5 text-gray-600 ${busy ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      {/* ALERTS */}
      {(localSuccess || localError || error) && (
        <div
          className={`p-4 rounded-[24px] border flex items-center gap-3 animate-in fade-in slide-in-from-top-4 ${
            localSuccess
              ? "bg-green-50 border-green-100 text-green-800"
              : "bg-red-50 border-red-100 text-red-800"
          }`}
        >
          {localSuccess ? (
            <CheckCircle2 className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <p className="text-sm font-bold tracking-tight">
            {localSuccess || localError || safeMsg(error)}
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-8 space-y-8">

          {/* ── STEP 1: Personal Information ─────────────────────────── */}
          <Section title="Step 1 — Personal Information" icon={User}>
            <div className="grid md:grid-cols-2 gap-x-12">
              <div className="space-y-1">
                <Row k="Title"       v={app?.title} />
                <Row k="Surname"     v={app?.surname} />
                <Row k="Other Names" v={app?.other_names} />
                <Row k="Gender"      v={app?.gender} />
                <Row k="Date of Birth" v={app?.date_of_birth} />
                <Row k="Nationality" v={app?.nationality} />
              </div>
              <div className="space-y-1">
                <Row k="Email"   v={app?.email} />
                <Row k="Phone"   v={app?.phone} />
                <Row k="State"   v={app?.state?.name} />
                <Row k="LGA"     v={app?.lga?.name} />
                <Row k="Sector"  v={app?.sector?.name} />
                <Row k="Chapter" v={app?.chapter?.name ?? app?.chapter_other} />
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-x-12">
              <div className="space-y-1">
                <Row k="Residential Address" v={app?.residential_address} />
              </div>
              <div className="space-y-1">
                <Row k="Postal Address" v={app?.postal_address} />
              </div>
            </div>

            {/* Document uploads */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <DocCard
                label="Passport Photograph"
                url={fileUrl(app?.passport_photo)}
                required
              />
              <DocCard
                label="Digital Signature"
                url={fileUrl(app?.signature)}
                required
              />
            </div>
          </Section>

          {/* ── STEP 2: Academic Qualifications ──────────────────────── */}
          <Section title="Step 2 — Academic Qualifications" icon={GraduationCap}>
            <div className="space-y-4">
              {(app?.qualifications || []).length === 0 ? (
                <p className="text-center py-6 text-gray-400 text-xs font-bold uppercase">
                  No qualifications listed
                </p>
              ) : (
                (app?.qualifications || []).map((q: any) => (
                  <div
                    key={q.id}
                    className="p-5 bg-gray-50 rounded-[24px] border border-gray-100 group hover:bg-white hover:shadow-lg transition-all"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <p className="text-sm font-black text-gray-900 uppercase tracking-tight">
                          {q.qualification}
                        </p>
                        <p className="text-xs font-bold text-gray-500 tracking-tight mt-0.5">
                          {q.institution} • {q.year}
                        </p>
                      </div>
                      <DocCard
                        label="Certificate"
                        url={fileUrl(q.cert_file ?? q.cert_url)}
                        compact
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </Section>

          {/* ── STEP 3: O-Level Results ───────────────────────────────── */}
          <Section title="Step 3 — O-Level Results" icon={Award}>
            <div className="grid md:grid-cols-2 gap-4">
              {(app?.o_level_results || []).length === 0 ? (
                <p className="col-span-2 text-center py-6 text-gray-400 text-xs font-bold uppercase">
                  No O-Level results recorded
                </p>
              ) : (
                (app?.o_level_results || []).map((r: any, i: number) => (
                  <div
                    key={i}
                    className="p-5 border border-gray-100 bg-gray-50 rounded-[24px]"
                  >
                    <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      Examination Record
                    </div>
                    <div className="text-sm font-black text-gray-900 mb-1">
                      {r.exam_type} ({r.exam_year})
                    </div>
                    <div className="text-xs font-bold text-gray-500 mb-4 tracking-tighter">
                      NO: {r.exam_number}
                    </div>
                    <SubjectBadges subjects={r.subjects} />
                    {fileUrl(r.result_file) && (
                      <a
                        href={fileUrl(r.result_file)!}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block mt-4 text-xs font-black text-indigo-600 hover:underline"
                      >
                        View result scan
                      </a>
                    )}
                  </div>
                ))
              )}
            </div>
          </Section>

          {/* ── STEP 4: Professional Memberships ─────────────────────── */}
          <Section title="Step 4 — Professional Memberships" icon={ShieldCheck}>
            <div className="space-y-4">
              {(app?.professionalMemberships || app?.professional_memberships || []).length === 0 ? (
                <p className="text-center py-6 text-gray-400 text-xs font-bold uppercase">
                  No professional memberships recorded
                </p>
              ) : (
                (app?.professionalMemberships ?? app?.professional_memberships ?? []).map((m: any, i: number) => (
                  <div
                    key={m.id ?? i}
                    className="p-5 bg-gray-50 rounded-[24px] border border-gray-100 flex justify-between items-center group hover:bg-white hover:shadow-lg transition-all"
                  >
                    <div>
                      <p className="text-sm font-black text-gray-900 uppercase tracking-tight">
                        {m.body_name}
                      </p>
                      {m.stage_passed && (
                        <p className="text-xs font-bold text-gray-500 tracking-tight mt-0.5">
                          Stage: {m.stage_passed}
                        </p>
                      )}
                    </div>
                    <DocCard
                      label="Certificate"
                      url={m.certificate_path ? fileUrl(m.certificate_path) : null}
                      compact
                    />
                  </div>
                ))
              )}
            </div>
          </Section>

          {/* ── STEP 5: Work Experience ───────────────────────────────── */}
          <Section title="Step 5 — Work Experience" icon={Briefcase}>
            <div className="space-y-8 pl-4">
              {(app?.workExperiences || []).length === 0 ? (
                <p className="text-center py-6 text-gray-400 text-xs font-bold uppercase">
                  No work history recorded
                </p>
              ) : (
                (app?.workExperiences || []).map((w: any) => (
                  <div
                    key={w.id}
                    className="relative pl-8 border-l-2 border-gray-100 pb-2"
                  >
                    <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-white border-4 border-gray-900" />
                    <div className="text-sm font-black text-gray-900 tracking-tight uppercase">
                      {w.position}
                    </div>
                    <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-2">
                      {w.organization}
                    </div>
                    <div className="text-[10px] font-black text-gray-400 mb-3 uppercase tracking-tighter bg-gray-50 inline-block px-2 py-0.5 rounded">
                      {fmtDate(w.start_date)} —{" "}
                      {w.end_date ? fmtDate(w.end_date) : "Present"}
                    </div>
                    <p className="text-sm text-gray-500 leading-relaxed font-medium">
                      {w.responsibilities}
                    </p>
                  </div>
                ))
              )}
            </div>
          </Section>

          {/* ── STEP 6: Seminars & CPD ────────────────────────────────── */}
          <Section title="Step 6 — Seminars & CPD" icon={FileText}>
            <div className="space-y-3">
              {(app?.seminars || []).length === 0 ? (
                <p className="text-center py-6 text-gray-400 text-xs font-bold uppercase">
                  No seminars or CPD activities recorded
                </p>
              ) : (
                (app?.seminars || []).map((s: any, i: number) => (
                  <div
                    key={s.id ?? i}
                    className="p-4 bg-gray-50 rounded-[20px] border border-gray-100 flex items-center justify-between group hover:bg-white hover:shadow-md transition-all"
                  >
                    <div>
                      <p className="text-sm font-black text-gray-900 uppercase tracking-tight">
                        {s.title}
                      </p>
                      <p className="text-[10px] font-bold text-gray-400 mt-0.5 uppercase tracking-widest">
                        {s.location}{s.location && s.date ? " • " : ""}{fmtDate(s.date)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Section>

        </div>

        {/* RIGHT COLUMN */}
        <div className="lg:col-span-4 space-y-8 sticky top-6">
          <Section title="Review Overview" icon={Clock}>
            <div className="space-y-4">
              <div className="pb-4 border-b border-gray-100">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                  Current Lifecycle Status
                </p>
                <span
                  className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm ${
                    app?.status === "approved"
                      ? "bg-green-100 text-green-700"
                      : app?.status === "rejected"
                      ? "bg-red-100 text-red-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {app?.status || "Processing"}
                </span>
              </div>
              {app?.category?.name && (
                <div className="pb-4 border-b border-gray-100">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                    Application Category
                  </p>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-black uppercase tracking-widest">
                    <Award className="w-3 h-3" />
                    {app.category.name}
                  </span>
                </div>
              )}
              <Row k="Completion Step" v={app?.completion_step} />
              <Row k="Verification" v={`${approvedCount} of 2 Referees`} />
              {rejectedCount > 0 && (
                <div className="p-4 bg-red-50 rounded-[20px] flex items-center gap-3 border border-red-100">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <p className="text-[10px] font-black text-red-700 uppercase leading-tight">
                    Referee rejection detected. Review carefully.
                  </p>
                </div>
              )}
            </div>
          </Section>

          {/* Chapter Decision */}
          <Section title="Chapter Decision" icon={Briefcase}>
            {app?.chapter_accepted_at ? (
              <div className="p-5 bg-green-50 rounded-[24px] border border-green-100 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-green-400 uppercase tracking-widest">
                    Accepted
                  </span>
                  <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border bg-green-50 text-green-600 border-green-200">
                    Approved
                  </span>
                </div>
                <div className="text-sm font-black text-gray-900 tracking-tight">
                  {app.chapterAcceptedBy?.name ?? "—"}
                </div>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  {fmtDate(app.chapter_accepted_at)}
                </div>
              </div>
            ) : (
              <div className="p-5 bg-gray-50 rounded-[24px] border border-dashed border-gray-200 text-center">
                <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">
                  Awaiting Chapter Review
                </span>
              </div>
            )}
          </Section>

          <Section title="Referee Audit" icon={ShieldCheck}>
            <div className="space-y-4">
              {referees.map((r: any, idx: number) => {
                const decision = normalizeDecision(r.decision);
                return (
                  <div
                    key={idx}
                    className="p-5 bg-gray-50 rounded-[24px] border border-gray-100 space-y-3 group hover:bg-white hover:shadow-md transition-all"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">
                        Referee {idx + 1}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border ${
                          decision === "approved"
                            ? "bg-green-50 text-green-600 border-green-100"
                            : decision === "rejected"
                            ? "bg-red-50 text-red-600 border-red-100"
                            : "bg-white text-gray-400 border-gray-100"
                        }`}
                      >
                        {decision}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-black text-gray-900 tracking-tight">
                        {r?.member?.firstname} {r?.member?.surname}
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 italic mb-2">
                        "{r.relationship || "Colleague"}"
                      </div>
                      {r.remarks && (
                        <p className="text-[11px] text-gray-500 bg-white/50 p-2 rounded-lg border border-gray-50 border-dashed">
                          "{r.remarks}"
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* DECISION PANEL */}
          {(canReview || isMyReferral) && (
            <div className="bg-gray-900 rounded-[40px] p-8 shadow-2xl shadow-gray-200 ring-1 ring-white/10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse shadow-[0_0_10px_rgba(59,130,246,0.8)]" />
                <h3 className="text-xs font-black text-white uppercase tracking-[0.2em]">
                  Decision Control
                </h3>
              </div>

              {/* Admin Interface — only admins with the approve/reject privilege.
                  Approve and Reject used to live here; they are the Membership
                  Committee's decision now, and the endorsement → secretariat →
                  committee ladder is driven from the review screen. What is left
                  here is the one thing an admin reading the application still
                  needs to be able to do on the spot: send it back. */}
              {canReview && IN_REVIEW.includes(String(app?.status).toLowerCase()) && (
                  <div className="space-y-5">
                    <Link
                      to={`/probationer-applications/admin/${id}`}
                      className="block w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-[20px] text-[11px] font-black uppercase tracking-widest transition-all text-center shadow-lg shadow-blue-900/20"
                    >
                      Open review screen
                    </Link>
                    <p className="text-[10px] text-gray-500 text-center leading-relaxed">
                      This application is at{" "}
                      <span className="text-gray-300">{statusLabel(app?.status)}</span>. Sending it
                      to the Membership Committee — who approve or reject it — is done from the
                      review screen.
                    </p>

                    {/* Return to applicant for editing — compulsory statement,
                        recorded in the review trail + emailed to the applicant. */}
                    <div className="space-y-3 pt-5 border-t border-gray-800">
                      <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest">
                        Return for corrections
                      </p>
                      <textarea
                        className="w-full p-4 bg-gray-800 border border-gray-700 rounded-[20px] text-xs text-white placeholder-gray-500 outline-none focus:ring-1 focus:ring-amber-500 transition-all min-h-[100px]"
                        placeholder="Statement to the applicant (required) — what must they correct before resubmitting?"
                        value={returnStatement}
                        onChange={(e) => setReturnStatement(e.target.value)}
                      />
                      <button
                        onClick={onReturn}
                        disabled={busy || !returnStatement.trim()}
                        className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-gray-900 rounded-[20px] text-[11px] font-black uppercase tracking-widest transition-all disabled:opacity-30"
                      >
                        Return for Editing
                      </button>
                    </div>
                  </div>
                )}

              {/* Referee Interface */}
              {showRefereeForm && (
                <div className="space-y-5">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center border-b border-gray-800 pb-3">
                    Your recommendation is required
                  </p>
                  <textarea
                    className="w-full p-4 bg-gray-800 border border-gray-700 rounded-[20px] text-xs text-white placeholder-gray-500 outline-none focus:ring-1 focus:ring-blue-500 min-h-[100px]"
                    placeholder="Professional remarks..."
                    value={refRemarks}
                    onChange={(e) => setRefRemarks(e.target.value)}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => onRefAction("reject")}
                      className="py-4 bg-gray-800 text-red-500 rounded-[20px] text-[10px] font-black uppercase tracking-widest border border-gray-700"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => onRefAction("approve")}
                      className="py-4 bg-blue-600 text-white rounded-[20px] text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-900/20 hover:bg-blue-700"
                    >
                      Approve
                    </button>
                  </div>
                </div>
              )}

              {/* Finalized Message */}
              {!showRefereeForm && isMyReferral && (
                <div className="text-center py-4 px-4 bg-gray-800/50 rounded-2xl border border-gray-700/50">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                    Decision Process Finalized
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
